import type { Migration as SchemaMigration } from '@schema';
import {
  MIGRATION_STATUS,
  MIGRATION_STATUS_TEXT,
  MIGRATION_STATUS_ICON,
  MIGRATION_STATUS_NORMALIZE,
  type MigrationStatus
} from '@/common/constants/migration.constants';
import { IP_V4_REGEXP, IP_V6_REGEXP } from '@/common/utils/validate';

// These interfaces extend the base API schema with fields present in the actual
// API response. Replace with @schema types once the backend schema package is updated.
interface SchemaInstanceNic {
  id?: string;
  mac_address?: string;
  name?: string;
  network_name?: string;
  ip_addresses?: string[];
}

interface SchemaInstanceDisk {
  size_bytes?: number;
  unit_number?: number;
  id?: string;
  controller_id?: string;
  path?: string;
  format?: string;
  storage_backend_identifier?: string;
  allocation_type?: string;
}

interface SchemaInstanceDeploymentConfig {
  disk_bus?: string;
  os_distro?: string;
  data_transfer_mechanism?: string;
  flavor_name?: string;
}

interface SchemaInstance {
  name?: string;
  path?: string;
  state?: string;
  os_type?: string;
  os_distro?: string;
  hostname?: string;
  guest_tools?: { state?: string; version?: string };
  os_version?: string;
  uefi?: boolean;
  num_cpu?: number;
  memory_mb?: number;
  storage_gb?: number;
  disks_num?: number;
  nics?: SchemaInstanceNic[];
  disks?: SchemaInstanceDisk[];
  public_ip_address?: string;
  instance_deployment_info?: {
    config?: SchemaInstanceDeploymentConfig;
  };
}

type SchemaMigrationExt = SchemaMigration & {
  target_project?: { name?: string; domain_id?: string; domain_name?: string; id?: string };
  domain_name?: string;
  project_name?: string;
  storage?: string | number;
  instances?: Record<string, SchemaInstance>;
  last_execution_status?: string;
  origin_endpoint_id?: string;
  created_at?: string;
  updated_at?: string | null;
  transfer_result?: string | null;
  source_environment?: {
    automatically_enable_cbt?: boolean;
    export_hostname_as_instance_name?: boolean;
    vixdisklib_compatibility_version?: string;
    datacenter?: string;
  };
  source_options?: {
    automatically_enable_cbt?: boolean;
    vixdisklib_compat_mode?: string;
    datacenter_name?: string;
  };
  target_environment?: {
    flavor_name?: string;
    disk_bus?: string;
    os_distro?: string;
    set_dhcp?: boolean;
    preserve_fixed_ips?: boolean;
    use_floating_ip?: boolean;
    preserve_floating_ip?: boolean;
    retain_user_credentials?: boolean;
    delete_disks_on_vm_termination?: boolean;
    machine_type?: string;
    keypair_name?: string;
    server_group?: string;
    floating_ip_pool?: string;
  };
  network_map?: Record<string, { id: string; type?: string }>;
  network_mapping?: Array<{ source: string; target: string }>;
  storage_mappings?: Record<string, {
    destination?: string;
    disk_id?: string;
  }> | {
    default?: string;
    backend_mappings?: Record<string, { destination?: string }>;
    disk_mappings?: Record<string, { destination?: string }>;
  };
  deployment_opts?: {
    clone_disks?: boolean;
    os_morphing?: boolean;
    save_snapshot?: boolean;
    force?: boolean;
    force_deployment?: boolean;
    start?: boolean;
  };
};

export interface MigrationNetworkInterface {
  mac: string;
  ipAddress: string;
  network: string;
  type: string;
}

export interface MigrationDisk {
  id: number;
  size: number;
  allocatedSize: number;
  backendIdentifier: string;
  format: string;
  guestDevice: string;
}

export interface MigrationSourceVM {
  vmId: string;
  name: string;
  hostname: string;
  uefi: boolean;
  state: string;
  osType: string;
  osDistro: string;
  osVersion: string;
  guestTools: string;
  cores: number;
  memory: number;
  networkInterfaces: MigrationNetworkInterface[];
  disks: MigrationDisk[];
}

export interface MigrationTargetMapping {
  networkMappings: {
    source: string;
    target: string;
    type: string;
  }[];
  diskMappings: {
    source: string;
    target: string;
    storage: string;
  }[];
}

export interface MigrationSourceOptions {
  os: string;
  vCPU: number;
  ram: number;
  disksCount: number;
  disksSize: number;
  automaticallyEnableCBT: boolean;
  useVMHostname: boolean;
  vixDiskLibCompatibility: string;
  datacenterName: string;
}

export interface MigrationTargetOptions {
  flavorName: string;
  diskBus: string;
  osDistro: string;
  setDhcp: boolean;
  preserveFixedIps: boolean;
  useFloatingIp: boolean;
  preserveFloatingIp: boolean;
  retainUserCredentials: boolean;
  deleteDiskOnTermination: boolean;
  machineType: string;
  keypairName: string;
  serverGroup: string;
  floatingIpPool: string;
}

export interface MigrationDeploymentOpts {
  cloneDisks: boolean;
  force: boolean;
  osMorphing: boolean;
  saveSnapshot: boolean;
  start: boolean;
}

export interface Migration {
  id: string;
  name: string;
  status: MigrationStatus;
  sourceCloudId: string;
  sourceCloudName: string;
  domain: string;
  project: string;
  storageSize: number;
  transferId: string;
  createdAt: string;
  updatedAt: string;
  sourceOptions: MigrationSourceOptions;
  targetOptions: MigrationTargetOptions;
  sourceVMs: MigrationSourceVM[];
  targetMappings: MigrationTargetMapping;
  deploymentOpts: MigrationDeploymentOpts;
}

export type MappedMigration = Migration & {
  statusText: string;
  statusIcon: string;
};

const VM_STATE_LABELS: Record<string, string> = {
  poweredOn: 'Active',
  poweredOff: 'Stopped',
  suspended: 'Suspended',
  running: 'Active',
  stopped: 'Stopped',
  shutoff: 'Stopped',
  paused: 'Paused'
};

const firstIPAddress = (addresses: string[] | undefined): string => {
  if (!addresses?.length) return '';
  return addresses.find(ip => IP_V4_REGEXP.test(ip))
    ?? addresses.find(ip => IP_V6_REGEXP.test(ip))
    ?? addresses[0];
};

const mapDiskMappings = (storageMappings: SchemaMigrationExt['storage_mappings']): MigrationTargetMapping['diskMappings'] => {
  if (!storageMappings || typeof storageMappings !== 'object') return [];

  if ('disk_mappings' in storageMappings && storageMappings.disk_mappings) {
    const defaultDest = ('default' in storageMappings ? storageMappings.default : '') ?? '';
    return Object.entries(storageMappings.disk_mappings).map(([diskId, mapping]) => ({
      source: diskId,
      target: mapping.destination ?? defaultDest,
      storage: mapping.destination ?? defaultDest
    }));
  }

  if ('backend_mappings' in storageMappings && storageMappings.backend_mappings) {
    const defaultDest = ('default' in storageMappings ? storageMappings.default : '') ?? '';
    return Object.entries(storageMappings.backend_mappings).map(([backendId, mapping]) => ({
      source: backendId,
      target: mapping.destination ?? defaultDest,
      storage: mapping.destination ?? defaultDest
    }));
  }

  return [];
};

const mapper = (m: SchemaMigrationExt): MappedMigration => {
  const instanceEntries = Object.entries(m.instances ?? {});

  const item: Migration = {
    id: m.id ?? '',
    name: m.name ?? (Object.values(m.instances ?? {}).map(i => i.name).filter(Boolean).join(', ')
      || Object.keys(m.instances ?? {}).join(', ')),
    status: MIGRATION_STATUS_NORMALIZE[m.last_execution_status ?? m.status ?? ''] ?? MIGRATION_STATUS.IN_PROGRESS,
    sourceCloudId: m.origin_endpoint_id ?? m.source_cloud ?? '',
    sourceCloudName: m.origin_endpoint_id ?? m.source_cloud ?? '',
    domain: m.target_project?.domain_name ?? m.domain_name ?? '',
    project: m.target_project?.name ?? m.project_name ?? '',
    storageSize: typeof m.storage === 'string' ? parseInt(m.storage, 10) || 0 : 0,
    transferId: m.id ?? '',
    createdAt: m.created_at ?? '',
    updatedAt: m.updated_at ?? '',
    sourceOptions: {
      os: instanceEntries[0]?.[1].os_type ?? '',
      vCPU: instanceEntries.reduce((sum, [_k, v]) => sum + (v.num_cpu ?? 0), 0),
      ram: instanceEntries.reduce((sum, [_k, v]) => sum + (v.memory_mb ?? 0), 0) * 1024 * 1024,
      disksCount: instanceEntries.reduce((sum, [_k, v]) => sum + (v.disks_num ?? 0), 0),
      disksSize: instanceEntries.reduce((sum, [_k, v]) => sum + (v.storage_gb ?? 0), 0) * 1024 * 1024 * 1024,
      automaticallyEnableCBT: m.source_environment?.automatically_enable_cbt ?? m.source_options?.automatically_enable_cbt ?? false,
      useVMHostname: m.source_environment?.export_hostname_as_instance_name ?? false,
      vixDiskLibCompatibility: m.source_environment?.vixdisklib_compatibility_version ?? m.source_options?.vixdisklib_compat_mode ?? '',
      datacenterName: instanceEntries[0]?.[1]?.path?.split('/')[0] ?? ''
    },
    targetOptions: {
      flavorName: m.target_environment?.flavor_name ?? '',
      diskBus: m.target_environment?.disk_bus ?? '',
      osDistro: m.target_environment?.os_distro ?? '',
      setDhcp: m.target_environment?.set_dhcp ?? false,
      preserveFixedIps: m.target_environment?.preserve_fixed_ips ?? false,
      useFloatingIp: m.target_environment?.use_floating_ip ?? false,
      preserveFloatingIp: m.target_environment?.preserve_floating_ip ?? false,
      retainUserCredentials: m.target_environment?.retain_user_credentials ?? false,
      deleteDiskOnTermination: m.target_environment?.delete_disks_on_vm_termination ?? false,
      machineType: m.target_environment?.machine_type ?? '',
      keypairName: m.target_environment?.keypair_name ?? '',
      serverGroup: m.target_environment?.server_group ?? '',
      floatingIpPool: m.target_environment?.floating_ip_pool ?? ''
    },
    sourceVMs: instanceEntries.map(([vmId, v]) => ({
      vmId,
      name: v.name ?? '',
      hostname: v.hostname ?? '',
      uefi: v.uefi ?? false,
      state: VM_STATE_LABELS[v.state ?? ''] ?? v.state ?? '',
      osType: v.os_type ?? '',
      osDistro: v.os_distro ?? '',
      osVersion: v.os_version ?? '',
      guestTools: v.guest_tools
        ? [v.guest_tools.state, v.guest_tools.version ? `v${v.guest_tools.version}` : ''].filter(Boolean).join(' ')
        : '',
      cores: v.num_cpu ?? 0,
      memory: (v.memory_mb ?? 0) * 1024 * 1024,
      networkInterfaces: (v.nics ?? []).map(n => ({
        mac: n.mac_address ?? '',
        ipAddress: firstIPAddress(n.ip_addresses) || v.public_ip_address || '',
        network: n.network_name ?? '',
        type: n.name ?? ''
      })),
      disks: (v.disks ?? []).map(d => ({
        id: parseInt(d.id ?? '0', 10) || 0,
        size: d.size_bytes ?? 0,
        allocatedSize: 0,
        backendIdentifier: d.storage_backend_identifier ?? '',
        format: d.format ?? '',
        guestDevice: d.path ?? d.unit_number?.toString() ?? ''
      }))
    })),
    targetMappings: {
      networkMappings: m.network_map
        ? Object.entries(m.network_map).map(([source, target]) => ({
          source,
          target: target.id,
          type: target.type ?? ''
        }))
        : (m.network_mapping ?? []).map(n => ({ source: n.source, target: n.target, type: '' })),
      diskMappings: mapDiskMappings(m.storage_mappings)
    },
    deploymentOpts: {
      cloneDisks: m.deployment_opts?.clone_disks ?? false,
      force: m.deployment_opts?.force ?? m.deployment_opts?.force_deployment ?? false,
      osMorphing: m.deployment_opts?.os_morphing ?? false,
      saveSnapshot: m.deployment_opts?.save_snapshot ?? false,
      start: m.deployment_opts?.start ?? false
    }
  };

  return {
    ...item,
    statusText: MIGRATION_STATUS_TEXT[item.status],
    statusIcon: MIGRATION_STATUS_ICON[item.status]
  };
};

export default mapper;
